import { Locale } from '@longy/common'
import { BlockDefinitionArray } from '@longy/common/cms'
import { PageRenderer } from '@longy/web-components/cms'
import { CmsSiteKey } from '@prisma-clinic/client'
import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import i18n from 'i18next'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { CmsLocaleNotice } from '../components/cms/CmsLocaleNotice'
import { Footer } from '../components/landing/Footer'
import { Header } from '../components/landing/Header'
import type { ApiUtils } from '../core/trpc'
import { buildCmsMissHead, buildCmsPageHead, readSiteOrigin } from '../utils/cmsHead'

const SITE_KEY = CmsSiteKey.LONGY

/** The splat arrives with no leading slash already, but a trailing one is still possible. */
const toStoredSlug = (splat: string | undefined): string =>
    (splat ?? '').replace(/^\/+/, '').replace(/\/+$/, '')

const loadPage = async (apiUtils: ApiUtils, slug: string) => {
    try {
        return await apiUtils.cmsPage.getBySlug.ensureData({ siteKey: SITE_KEY, slug })
    } catch {
        // A throw is a miss, not an error. Legacy urls like `regi-oldal.html` fail slug validation
        // on the way in, and they are exactly the ones the redirect table exists to catch.
        return null
    }
}

const CmsPage = (): ReactElement => {
    const data = Route.useLoaderData()
    const { t } = useTranslation()

    if (!data || data.kind === 'gone' || !data.page) {
        return (
            <div className="flex min-h-screen flex-col">
                <Header />
                <main className="mx-auto flex w-full max-w-3xl grow flex-col items-center justify-center gap-4 px-6 py-24 text-center">
                    <h1 className="text-3xl font-bold">{t('frontend.cms.goneTitle')}</h1>
                    <p className="text-brand-gray">{t('frontend.cms.goneBody')}</p>
                </main>
                <Footer />
            </div>
        )
    }

    return (
        <div className="flex min-h-screen flex-col">
            <Header />
            <CmsLocaleNotice
                pageLocale={data.page.locale as Locale}
                localizedSlugs={data.page.localizedSlugs}
            />
            <main className="grow">
                {/*
                 * The same renderer the admin preview mounts. `baseUrl` stays empty so hrefs are
                 * relative, and `isPreview` stays false so every surface behaves live.
                 */}
                <PageRenderer
                    blocks={data.page.blocks as BlockDefinitionArray}
                    locale={data.page.locale as Locale}
                />
            </main>
            <Footer />
        </div>
    )
}

export const Route = createFileRoute('/$')({
    component: CmsPage,
    /**
     * The miss ladder. Each step runs only when the previous missed, so a live page pays for
     * nothing below it.
     */
    loader: async ({ params, context }) => {
        const slug = toStoredSlug(params._splat)

        // 1. The page itself.
        const page = await loadPage(context.apiUtils, slug)
        if (page) {
            const websiteSettings = await context.apiUtils.websiteSettings.getPublic
                .ensureData()
                .catch(() => null)
            /*
             * `blocks` crosses this boundary opaquely and is narrowed back in the component.
             *
             * The router types serialized loader data by mapping `unknown` to `{}`, and the stored
             * rich-text document's `content` is deliberately `unknown[]` — the shallow shape that
             * keeps the form libraries' deep-key recursion from blowing up. The two cannot be
             * reconciled at the type level, and the runtime value is plain json either way.
             */
            const { blocks, ...rest } = page
            return {
                kind: 'page' as const,
                page: { ...rest, blocks: blocks as object[] },
                websiteSettings,
            }
        }

        // 2. A redirect. An external target passes through verbatim; an internal one is already
        //    canonical by the time the api returns it.
        const redirection = await context.apiUtils.cmsRedirection.resolve
            .ensureData({ siteKey: SITE_KEY, matchUrl: slug })
            .catch(() => null)

        if (redirection?.targetUrl) {
            throw redirect({ href: redirection.targetUrl, statusCode: 301, throw: true })
        }

        // 3. Was this url ever live? 410 tells a crawler the content was removed on purpose, which
        //    de-indexes faster than a 404 and does not read as a site fault.
        const wasPublished = await context.apiUtils.cmsPage.wasPublishedAt
            .ensureData({ siteKey: SITE_KEY, slug })
            .catch(() => false)

        if (wasPublished) {
            // The 410 itself is applied in `src/server.tsx`: a status set here does not survive,
            // because the route resolved successfully and the render's own 200 wins.
            return { kind: 'gone' as const, page: null, websiteSettings: null }
        }

        // 4. Nothing here, and nothing ever was. The router owns the 404 status.
        throw notFound()
    },
    /*
     * `head` cannot use a hook, so it reads the i18next instance directly — the same instance the
     * component's `t` is bound to, so the tab title and the body say the same thing in the same
     * language. The default is the last resort for a load where the translation fetch failed.
     */
    head: ({ loaderData }) => {
        if (!loaderData || loaderData.kind === 'gone' || !loaderData.page) {
            const key = loaderData?.kind === 'gone' ? 'goneTitle' : 'notFoundTitle'
            return buildCmsMissHead(
                i18n.t(`frontend.cms.${key}`, { defaultValue: 'A tartalom nem elérhető' }),
            )
        }
        return buildCmsPageHead(loaderData.page, loaderData.websiteSettings, readSiteOrigin())
    },
})
